import { LockKeyhole } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { MainChapterSelection } from "@/features/book-assembly/book-assembly.types";

type ChapterSelectionStepProps = {
  chapters: MainChapterSelection[];
  onToggle: (chapterId: string, included: boolean) => void;
};

export function ChapterSelectionStep({
  chapters,
  onToggle,
}: ChapterSelectionStepProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {t("bookAssembly.steps.chapterSelection.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">
            {t("bookAssembly.steps.chapterSelection.title")}
          </h2>
          <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
            {t("bookAssembly.steps.chapterSelection.body")}
          </p>
        </div>

        <div className="divide-y divide-ink/10 border-y border-ink/10">
          {chapters.map((chapter) => (
            <div
              className="flex flex-col gap-3 py-5 md:flex-row md:items-center md:justify-between"
              key={chapter.chapterId}
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">
                    {t("bookAssembly.common.chapterNumber", {
                      count: chapter.chapterNumber,
                    })}
                  </Badge>
                  <Badge variant="secondary">{chapter.statusLabel}</Badge>
                  {chapter.locked ? (
                    <Badge className="gap-1" variant="default">
                      <LockKeyhole className="h-3 w-3" />
                      {t("bookAssembly.common.locked")}
                    </Badge>
                  ) : null}
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground">{chapter.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {chapter.isOpening
                      ? t("bookAssembly.steps.chapterSelection.opening")
                      : chapter.isClosing
                        ? t("bookAssembly.steps.chapterSelection.closing")
                        : t("bookAssembly.steps.chapterSelection.optional")}
                  </p>
                </div>
              </div>

              <label className="inline-flex items-center gap-3 text-sm font-medium text-foreground">
                <input
                  checked={chapter.included}
                  className="h-4 w-4 rounded border-input accent-primary"
                  disabled={chapter.locked}
                  onChange={(event) =>
                    onToggle(chapter.chapterId, event.currentTarget.checked)
                  }
                  type="checkbox"
                />
                {t("bookAssembly.steps.chapterSelection.include")}
              </label>
            </div>
          ))}
        </div>
    </section>
  );
}
